import React, { useCallback, useMemo, useState } from 'react';
import { topAchievementInsights } from 'apps/sigma2-ui/src/models/user';
import { topRiskInsights } from 'apps/sigma2-ui/src/models/user';
import { topTreatments } from 'apps/sigma2-ui/src/models/user';
import { TreatmentSelectionModal } from 'apps/sigma2-ui/src/components/TreatmentSelectionModal';
import { KeyTakeawayCardStack } from 'apps/sigma2-ui/src/pages/FocusAreaPage/KeyTakeawayCardStack';
import { SolutionCardStack } from 'apps/sigma2-ui/src/components/overview/SolutionsCardStack';
import { TalentCalibrationScoreCard } from 'apps/sigma2-ui/src/components/overview/TalentCalibrationScoreCard';
import { useToggle } from 'apps/sigma2-ui/src/lib/hooks/useToggle';
import { UpdateTreatmentOptions } from 'apps/sigma2-ui/src/state/observation';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { getTestCounts } from 'libs/end-to-end/src/tests-utils';
import { getTestCountsByFocusArea } from 'libs/end-to-end/src/tests-utils';
import { getTotalTestCount } from 'libs/end-to-end/src/tests-utils';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { MyUser } from 'libs/end-to-end/src/my-user';
import { TestCount } from 'libs/end-to-end/src/tests-utils';
import { Treatment } from 'libs/end-to-end/src/treatment';
import { getCalibrationScore } from 'libs/end-to-end/src/tests-utils';
import { DashboardFocusAreaSummary } from 'apps/sigma2-ui/src/components/overview/DashboardFocusAreaSummary';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Divider } from 'apps/sigma2-ui/src/components/ui/Divider';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { OverviewPageConfiguration } from 'libs/end-to-end/src/overview-page-configuration';
import { Phrases } from 'libs/end-to-end/src/phrases';
import { usePagination } from 'apps/sigma2-ui/src/lib/hooks/usePagination';
import { PaginationButtons } from 'apps/sigma2-ui/src/components/ui/PaginationButtons';
import { TopOpportunityCard } from 'apps/sigma2-ui/src/components/TopOpportunityCard';
import { Nullable } from 'libs/end-to-end/src/ui-types';
import { DashboardNoIntelligenceSummary } from 'apps/sigma2-ui/src/components/overview/DashboardNoIntelligenceSummary';
import { sortTopThreeOpportunities } from 'apps/sigma2-ui/src/lib/top-opportunities';
import { TopOpportunity } from 'apps/sigma2-ui/src/lib/top-opportunities';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { RecommendedSolutionCard } from 'apps/sigma2-ui/src/components/ui/RecommendedSolutionCard';
import { TopOpportunitiesSection } from 'apps/sigma2-ui/src/components/TopOpportunitiesSection';
import { findDimensionForLevel } from 'libs/end-to-end/src/deep-dive-section-utils';
import { FilterDefinitionForExtras } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { determineSecondaryFilterDefinitionsForFeature } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { BiasAnalysisRequest } from 'libs/end-to-end/src/custom-viz/BiasAnalysisTypes';
import { UserAuthenticatedApiCall } from 'apps/sigma2-ui/src/state/user-types';
import { NavigateSafeFn } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { callGenerateShareLink } from 'apps/sigma2-ui/src/state/generate-share-link';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { isEntitledToBiasAnalysis } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToDashboards } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToTableView } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToHeatmap } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToHiringCopilot } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToProgress } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToPromotionCopilot } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToSavedAnalysis } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToSuccessFactors } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToSolutions } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToPayBenchmarking } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToPayCorrection } from 'libs/end-to-end/src/group-entitlements';
import { isCustomChartsEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isDashboardsEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isHeatmapEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isProgressEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isHiringCopilotEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isKeyFactorsEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isPayBenchmarkingEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isPayCorrectionEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isPromotionCopilotEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';

interface DashboardViewProps {
  myUser: MyUser | null;
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  organizationId: string | null;
  myObservation: MyObservation | null;
  updateTreatments: (
    treatmentUpdates: Treatment[],
    options?: UpdateTreatmentOptions,
  ) => void;
  language: Phrases;
  overviewPageConfiguration: OverviewPageConfiguration;
  navigate: NavigateSafeFn;
}

const getTreatmentIndexForTopOpportunity = ({
  opportunity,
  treatments,
}: {
  opportunity: TopOpportunity;
  treatments: Treatment[];
}): Nullable<number> /* index */ => {
  let index = null;

  for (const treatment of treatments) {
    if (
      opportunity.treatmentLevelDefinition.columnValue === treatment.level &&
      opportunity.metricDefinition.columnHeaderValue === treatment.metric
    ) {
      index = treatments.indexOf(treatment);
      break;
    }
  }

  return index;
};

// eslint-disable-next-line complexity
export const DashboardView = ({
  userAuthenticatedApiCall,
  organizationId,
  myObservation,
  updateTreatments,
  overviewPageConfiguration,
  language,
  navigate,
}: DashboardViewProps) => {
  /**
   * Used for top opportunities section only, references myObservation.treatments
   * for the index
   */
  const [selectedTopTreatmentIndex, setSelectedTopTreatmentIndex] =
    useState<Nullable<number>>(null);

  const dimensionsConfigPayload = useDimensionConfig();

  const analytics = useAnalytics();

  /**
   * Used for executive summary section only
   */
  const [solutionSelectionModalVisible, toggleSolutionSelectionModalVisible] =
    useToggle();

  const counts: TestCount | null = useMemo(() => {
    if (!myObservation?.testSummary) {
      return {
        total: 0,
        passing: 0,
        incomplete: 0,
        failing: 0,
      };
    }
    return getTestCounts(
      myObservation.testSummary.tests.filter((t) => {
        return isEntitledToBiasAnalysis({
          features: myObservation?.features ?? [],
          focusArea: t.focusArea,
        });
      }),
    );
  }, [myObservation]);

  const applicableFocusAreas = useMemo(() => {
    return myObservation?.focusAreas?.map((f) => f.focusArea) ?? [];
  }, [myObservation]);

  const allFocusAreaDefinitions = useMemo(() => {
    return myObservation?.focusAreas ?? [];
  }, [myObservation]);

  const worstPerformingFocusArea = useMemo(() => {
    if (!applicableFocusAreas.length) {
      return 'Hiring';
    }

    let lowestScore = Number.MAX_VALUE;
    let worstFocusArea: FocusArea = applicableFocusAreas[0];

    for (const focusArea of applicableFocusAreas) {
      const t = getTestCountsByFocusArea(
        myObservation?.testSummary?.tests ?? [],
        focusArea,
      );
      // We want the worst, so let's use optimization as a baseline.
      const score = getCalibrationScore({
        testCount: t,
      });
      if (score < lowestScore) {
        lowestScore = score;
        worstFocusArea = focusArea;
      }
    }

    return worstFocusArea;
  }, [myObservation, applicableFocusAreas]);

  const achievements = useMemo(() => {
    return topAchievementInsights({
      insights: myObservation?.insights || null,
      metric: null,
      focusArea: worstPerformingFocusArea,
      allFocusAreaDefinitions,
    });
  }, [allFocusAreaDefinitions, myObservation, worstPerformingFocusArea]);
  const risks = useMemo(() => {
    return topRiskInsights({
      insights: myObservation?.insights || null,
      metric: null,
      focusArea: worstPerformingFocusArea,
      allFocusAreaDefinitions,
    });
  }, [allFocusAreaDefinitions, myObservation, worstPerformingFocusArea]);

  const sortedCards = useMemo(() => {
    return [...achievements, ...risks]
      .sort((a, b) => a.insight.priority - b.insight.priority)
      .slice(0, 5);
  }, [achievements, risks]);

  const topSixTreatments = useMemo(() => {
    if (!myObservation || myObservation.hideFocusAreaSolutionsPage) {
      return [];
    }
    return topTreatments({ myObservation });
  }, [myObservation]);
  const topThreeTreatments = useMemo(() => {
    return topSixTreatments.slice(0, 3);
  }, [topSixTreatments]);

  const solutionCardPagination = usePagination({
    totalPages: topThreeTreatments.length,
  });

  const calibrationScore = useMemo(() => {
    if (!counts || !myObservation) {
      return 0;
    }
    return getCalibrationScore({
      testCount: counts,
    });
  }, [counts, myObservation]);

  const showNewDataIntelligenceDeepDivePage = useMemo(() => {
    return myObservation?.showNewDataIntelligenceDeepDivePage ?? false;
  }, [myObservation?.showNewDataIntelligenceDeepDivePage]);

  const { allLevels } = useDimensionConfig();

  const topThreeOpportunities: TopOpportunity[] = useMemo(() => {
    return sortTopThreeOpportunities({
      insights:
        myObservation?.insightsV2.filter((insight) => {
          return isEntitledToBiasAnalysis({
            features: myObservation?.features ?? [],
            focusArea: insight.focusArea,
          });
        }) || [],
      focusAreaDefinitions: myObservation?.focusAreas || [],
      allLevels: allLevels,
      showMetricsWithoutBiasTestTasks: false,
    });
  }, [allLevels, myObservation]);

  /**
   * Used for top opportunities section only
   */
  const onTreatmentClick = useCallback(
    (newSelectedTopTreatmentIndex: number) => {
      const treatment = myObservation?.treatments
        ? myObservation.treatments[newSelectedTopTreatmentIndex]
        : null;
      if (treatment) {
        analytics.trackEvent({
          eventName: 'Recommended Solution Card Clicked',
          eventProperties: {
            currentPage: 'Overview Page',
            focusArea: treatment.focusArea,
          },
        });
      }
      setSelectedTopTreatmentIndex(newSelectedTopTreatmentIndex);
    },
    [analytics, myObservation],
  );

  const areSolutionsEnabled = useMemo(() => {
    if (!myObservation) {
      return false;
    }

    return (
      !myObservation.hideFocusAreaSolutionsPage &&
      isEntitledToSolutions({
        features: myObservation?.features ?? [],
        focusArea: null,
      })
    );
  }, [myObservation]);

  const onTopOpportunityCardClick = useCallback(
    async (opportunity: TopOpportunity) => {
      const treatmentDimensionLevel =
        opportunity.treatmentLevelDefinition.columnValue;

      const dimension = findDimensionForLevel({
        allDimensions: dimensionsConfigPayload.allDimensions,
        level: treatmentDimensionLevel,
      })?.columnHeaderValue;

      if (!dimension || !myObservation || !organizationId) {
        return;
      }
      // Anytime top opportunities get clicked, they are in the context of all
      // filters applied. So by doing this, we receive all of the filter options
      // and apply them to the custom viz request
      const allPossibleSecondaryFilterSelections: FilterDefinitionForExtras[] =
        determineSecondaryFilterDefinitionsForFeature({
          feature: 'dataIntelligenceDeepDive',
          focusArea: opportunity.focusAreaDefinition.focusArea,
          analysisName: undefined,
          // make this a prop
          focusAreas: myObservation.focusAreas,
          filterSetDefinitionsWithValues:
            myObservation.filterSetDefinitionsWithValues,
          dimensionDefinitions: dimensionsConfigPayload.allDimensions,
          pipelineAnalysisConfiguration:
            myObservation.pipelineAnalysisConfiguration,
        }).map((definition) => {
          return {
            columnHeaderValue: definition.columnHeaderValue,
            values: definition.uniqueValues,
          };
        });

      const request: BiasAnalysisRequest = {
        inputs: {
          dropdowns: {},
          extras: {
            metric: opportunity.metricDefinition.columnHeaderValue,
            dimension,
            focusArea: opportunity.focusAreaDefinition.focusArea,
            filters: allPossibleSecondaryFilterSelections,
            treatmentDimensionLevel,
          },
        },
        customVizId: 'biasAnalysis',
        organizationId: organizationId,
        observationLocationType: myObservation.type,
      };

      const shareLinkResult = await callGenerateShareLink({
        windowUrl: window.location.origin,
        customVizRequest: request,
        userAuthenticatedApiCall,
      });
      if (!shareLinkResult) return;

      analytics.trackEvent({
        eventName: 'Top opportunity clicked',
        eventProperties: {
          focusArea: opportunity.focusAreaDefinition.focusArea,
          dimension: dimension,
          metric: opportunity.metricDefinition.columnHeaderValue,
          level: treatmentDimensionLevel,
        },
      });

      navigate(shareLinkResult.shareLink);
    },
    [
      dimensionsConfigPayload.allDimensions,
      myObservation,
      organizationId,
      userAuthenticatedApiCall,
      analytics,
      navigate,
    ],
  );

  const topOpportunitySection = useMemo(
    () => (
      <TopOpportunitiesSection
        opportunities={topThreeOpportunities}
        metricDefinition={null}
        calibrationFraming={myObservation?.calibrationFraming ?? 'bias'}
        card={({ opportunity, key }) => {
          const treatmentIndex = getTreatmentIndexForTopOpportunity({
            opportunity,
            treatments: myObservation?.treatments ?? [],
          });

          const treatment =
            treatmentIndex !== null && myObservation?.treatments
              ? myObservation.treatments[treatmentIndex]
              : null;

          const recommendedSolutionsCard = treatment &&
            areSolutionsEnabled &&
            isEntitledToSolutions({
              features: myObservation?.features ?? [],
              focusArea: opportunity.focusAreaDefinition.focusArea,
            }) && (
              <RecommendedSolutionCard
                impactFactor={treatment.impactFactor}
                treatmentType={treatment.type}
                onClick={() =>
                  treatmentIndex && onTreatmentClick(treatmentIndex)
                }
              />
            );

          return (
            <TopOpportunityCard
              key={key}
              metricDefinition={opportunity.metricDefinition}
              focusAreaDefinition={opportunity.focusAreaDefinition}
              referenceLevelDefinition={opportunity.referenceLevelDefinition}
              treatmentLevelDefinition={opportunity.treatmentLevelDefinition}
              rawDisparityPercentage={opportunity.rawDisparityPercentage}
              controlledDisparityPercentage={
                opportunity.controlledDisparityPercentage
              }
              debiasedDisparityPercentage={
                opportunity.debiasedDisparityPercentage
              }
              cardIsClickable={true}
              onCardClick={() => onTopOpportunityCardClick(opportunity)}
              recommendedSolutionsCard={recommendedSolutionsCard}
              calibrationFraming={myObservation?.calibrationFraming ?? 'bias'}
            />
          );
        }}
      />
    ),
    [
      topThreeOpportunities,
      myObservation,
      areSolutionsEnabled,
      onTreatmentClick,
      onTopOpportunityCardClick,
    ],
  );

  const shouldShowTalentCalibrationScoreCard = useMemo(() => {
    return (
      overviewPageConfiguration.type !== 'no-intelligence' &&
      isEntitledToBiasAnalysis({
        features: myObservation?.features ?? [],
        focusArea: null,
      })
    );
  }, [myObservation?.features, overviewPageConfiguration.type]);

  const shouldShowKeyTakeawayCardStack = useMemo(() => {
    return (
      overviewPageConfiguration.type !== 'no-intelligence' &&
      isEntitledToBiasAnalysis({
        features: myObservation?.features ?? [],
        focusArea: null,
      }) &&
      sortedCards.length > 0
    );
  }, [
    myObservation?.features,
    overviewPageConfiguration.type,
    sortedCards.length,
  ]);

  const shouldShowSolutionCardStack = useMemo(() => {
    return (
      overviewPageConfiguration.type !== 'no-intelligence' &&
      isEntitledToSolutions({
        features: myObservation?.features ?? [],
        focusArea: null,
      }) &&
      topThreeTreatments.length > 0
    );
  }, [
    myObservation?.features,
    overviewPageConfiguration.type,
    topThreeTreatments,
  ]);

  const showExecutiveSummarySection = useMemo(() => {
    return (
      !showNewDataIntelligenceDeepDivePage &&
      (shouldShowKeyTakeawayCardStack ||
        shouldShowSolutionCardStack ||
        shouldShowTalentCalibrationScoreCard)
    );
  }, [
    shouldShowKeyTakeawayCardStack,
    shouldShowSolutionCardStack,
    shouldShowTalentCalibrationScoreCard,
    showNewDataIntelligenceDeepDivePage,
  ]);

  const achievementsAndRiskSection = useMemo(() => {
    if (myObservation) {
      return (
        <div className="grid grid-cols-3 gap-4">
          {shouldShowTalentCalibrationScoreCard && (
            <TalentCalibrationScoreCard
              calibrationScore={calibrationScore}
              totalTestsCount={getTotalTestCount(counts)}
              passedTestsCount={counts.passing}
              calibrationFraming={myObservation.calibrationFraming}
            />
          )}

          {shouldShowKeyTakeawayCardStack && (
            <KeyTakeawayCardStack
              items={sortedCards}
              calibrationFraming={myObservation.calibrationFraming}
            />
          )}

          {shouldShowSolutionCardStack && (
            <SolutionCardStack
              treatments={topThreeTreatments}
              onTreatmentTitleClick={toggleSolutionSelectionModalVisible}
              currentTreatmentIndex={solutionCardPagination.currentPageIndex}
              pagination={
                <PaginationButtons
                  {...solutionCardPagination}
                  totalPages={topThreeTreatments.length}
                />
              }
              allFocusAreaDefinitions={myObservation.focusAreas}
            />
          )}
        </div>
      );
    }
  }, [
    myObservation,
    shouldShowTalentCalibrationScoreCard,
    calibrationScore,
    counts,
    shouldShowKeyTakeawayCardStack,
    sortedCards,
    shouldShowSolutionCardStack,
    topThreeTreatments,
    toggleSolutionSelectionModalVisible,
    solutionCardPagination,
  ]);

  const showTopOpportunitiesSection = useMemo(() => {
    return (
      overviewPageConfiguration.type !== 'no-intelligence' &&
      showNewDataIntelligenceDeepDivePage &&
      isEntitledToBiasAnalysis({
        features: myObservation?.features ?? [],
        focusArea: null,
      })
    );
  }, [
    myObservation?.features,
    overviewPageConfiguration.type,
    showNewDataIntelligenceDeepDivePage,
  ]);

  const shouldShowForYouSection = useMemo(() => {
    return (
      overviewPageConfiguration.type !== 'no-intelligence' &&
      isEntitledToBiasAnalysis({
        features: myObservation?.features ?? [],
        focusArea: null,
      })
    );
  }, [myObservation?.features, overviewPageConfiguration.type]);

  const progressEnabled = useMemo(() => {
    return (
      isProgressEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToProgress({
        features: myObservation?.features ?? [],
        focusArea: null,
      })
    );
  }, [myObservation]);

  const heatmapEnabled = useMemo(() => {
    return (
      isHeatmapEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToHeatmap({
        features: myObservation?.features ?? [],
        focusArea: null,
      })
    );
  }, [myObservation]);

  const dashboardsEnabled = useMemo(() => {
    return (
      isDashboardsEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToDashboards({
        features: myObservation?.features ?? [],
      })
    );
  }, [myObservation]);

  const savedAnalysesEnabled = useMemo(() => {
    return (
      (myObservation?.showSavedAnalysesPage ?? false) &&
      isEntitledToSavedAnalysis({
        features: myObservation?.features ?? [],
      })
    );
  }, [myObservation?.features, myObservation?.showSavedAnalysesPage]);

  const keyFactorsAvailable = useMemo(() => {
    return (
      isKeyFactorsEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToSuccessFactors({
        features: myObservation?.features ?? [],
        focusArea: null,
      })
    );
  }, [myObservation]);

  const hiringCopilotEnabled = useMemo(() => {
    return (
      isHiringCopilotEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToHiringCopilot({
        features: myObservation?.features ?? [],
      })
    );
  }, [myObservation]);

  const promotionCopilotEnabled = useMemo(() => {
    return (
      isPromotionCopilotEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToPromotionCopilot({
        features: myObservation?.features ?? [],
      })
    );
  }, [myObservation]);

  const payCorrectionEnabled = useMemo(() => {
    return (
      isPayCorrectionEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToPayCorrection({
        features: myObservation?.features ?? [],
      })
    );
  }, [myObservation]);

  const payBenchmarkingEnabled = useMemo(() => {
    return (
      isPayBenchmarkingEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToPayBenchmarking({
        features: myObservation?.features ?? [],
      })
    );
  }, [myObservation]);

  const tableViewEnabled = useMemo(() => {
    return (
      isCustomChartsEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToTableView({
        features: myObservation?.features ?? [],
        focusArea: null,
      })
    );
  }, [myObservation]);

  const showNoIntelligenceSummary = useMemo(() => {
    return (
      progressEnabled ||
      heatmapEnabled ||
      dashboardsEnabled ||
      savedAnalysesEnabled ||
      keyFactorsAvailable ||
      hiringCopilotEnabled ||
      promotionCopilotEnabled ||
      payCorrectionEnabled ||
      payBenchmarkingEnabled ||
      tableViewEnabled
    );
  }, [
    dashboardsEnabled,
    heatmapEnabled,
    hiringCopilotEnabled,
    payBenchmarkingEnabled,
    payCorrectionEnabled,
    progressEnabled,
    promotionCopilotEnabled,
    savedAnalysesEnabled,
    keyFactorsAvailable,
    tableViewEnabled,
  ]);

  if (
    !organizationId ||
    !myObservation ||
    !myObservation.testSummary ||
    !counts
  ) {
    return <></>;
  }

  return (
    <Spacing size={'lg'} direction={'vertical'}>
      {overviewPageConfiguration.type !== 'no-intelligence' && (
        <>
          {showTopOpportunitiesSection && topOpportunitySection}
          {showExecutiveSummarySection && (
            <Spacing size={'lg'} direction={'vertical'}>
              <S2Typography
                variant="headline-5"
                translatedContent={language.executiveSummary()}
              />

              {achievementsAndRiskSection}
            </Spacing>
          )}

          <TreatmentSelectionModal
            maxWidthOverride="max-w-screen-md"
            show={
              showNewDataIntelligenceDeepDivePage
                ? selectedTopTreatmentIndex !== null
                : solutionSelectionModalVisible
            }
            title={language.focusAreaKeyTakeawayCardSolutionTitle()}
            topTreatments={{
              treatments: myObservation.treatments
                ? [
                    showNewDataIntelligenceDeepDivePage &&
                    selectedTopTreatmentIndex
                      ? myObservation.treatments[selectedTopTreatmentIndex]
                      : topThreeTreatments[
                          solutionCardPagination.currentPageIndex
                        ],
                  ]
                : [],
            }}
            updateTreatments={updateTreatments}
            onClose={
              showNewDataIntelligenceDeepDivePage
                ? () => setSelectedTopTreatmentIndex(null)
                : toggleSolutionSelectionModalVisible
            }
          />

          {(showTopOpportunitiesSection || showExecutiveSummarySection) &&
            (shouldShowForYouSection || showNoIntelligenceSummary) && (
              <div>
                <div className="p-6" />

                <Divider direction={'horizontal'} />
              </div>
            )}

          {shouldShowForYouSection && (
            <>
              <S2Typography
                variant="headline-5"
                translatedContent={language.forYou()}
              />

              <DashboardFocusAreaSummary
                testSummary={myObservation.testSummary}
                focusAreas={myObservation.focusAreas.filter((fa) => {
                  return isEntitledToBiasAnalysis({
                    features: myObservation?.features ?? [],
                    focusArea: fa.focusArea,
                  });
                })}
                calibrationFraming={myObservation.calibrationFraming}
              />
            </>
          )}
        </>
      )}
      {showNoIntelligenceSummary && (
        <Spacing size={'lg'} direction={'vertical'}>
          <DashboardNoIntelligenceSummary
            hiringCopilotEnabled={hiringCopilotEnabled}
            promotionCopilotEnabled={promotionCopilotEnabled}
            payCorrectionEnabled={payCorrectionEnabled}
            payBenchmarkingEnabled={payBenchmarkingEnabled}
            keyFactorsEnabled={keyFactorsAvailable}
            heatmapEnabled={heatmapEnabled}
            dashboardsEnabled={dashboardsEnabled}
            savedAnalysesEnabled={savedAnalysesEnabled}
            progressEnabled={progressEnabled}
            tableViewEnabled={tableViewEnabled}
          />
        </Spacing>
      )}
    </Spacing>
  );
};
